import React, { useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import * as Sentry from './utils/sentry';

import './App.css';

// Context Providers
import { AuthProvider } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
// ZegoProvider removed
import { SocketProvider } from './context/SocketContext';
import { CallProvider } from './context/CallContext';

// Layout Components
import MobileLayout from './components/MobileLayout';
import ErrorBoundary from './components/ErrorBoundary';

// Page Components
import SplashScreen from './pages/SplashScreen';
import Onboarding from './pages/Onboarding';
import RoleSelection from './pages/RoleSelection';
import Subscription from './pages/Subscription';
import DocumentUpload from './pages/onboarding/DocumentUpload';
import ProfileImageOnboarding from './pages/onboarding/ProfileImage';
import ApprovalWaiting from './pages/onboarding/ApprovalWaiting';

// Auth Pages
import PatientLogin from './pages/auth/PatientLogin';
import DoctorLogin from './pages/auth/DoctorLogin';
import RetailerLogin from './pages/auth/RetailerLogin';
import ClinicLogin from './pages/auth/ClinicLogin';
import HospitalLogin from './pages/auth/HospitalLogin';
// Legal & Static Pages
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import TermsAndConditions from './pages/legal/TermsAndConditions';
import SupportPage from './pages/legal/SupportPage';
import AboutUs from './pages/AboutUs';
import NotFound from './pages/NotFound';

// Registration Pages
import PatientRegister from './pages/auth/register/patient/Register';
import DoctorRegister from './pages/auth/register/doctor/Register';
import RetailerRegister from './pages/auth/register/retailer/Register';
import ClinicRegister from './pages/auth/register/clinic/Register';
import HospitalRegister from './pages/auth/register/hospital/Register';
import AdminLogin from './pages/auth/AdminLogin';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import VerifyPrescription from './pages/prescription/VerifyPrescription';
import ProtectedRoute from './components/ProtectedRoute';

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';
import AllUsers from './pages/admin/users/AllUsers';
import PendingUsers from './pages/admin/users/PendingUsers';
// Patient: Medicines are handled in MobileLayout.js

import Sliders from './pages/admin/content/Sliders';
import YogaVideos from './pages/admin/content/YogaVideos';
import Podcasts from './pages/admin/content/Podcasts';
import RetailerDashboard from './pages/retailer/dashboard/Dashboard';
import Withdrawals from './pages/admin/withdrawals/Withdrawals';
import SubscriptionPlans from './pages/admin/subscriptions/SubscriptionPlans';
import RetailerOrders from './pages/retailer/Orders';
import AdminSettings from './pages/admin/Settings';
import ConsultationRoom from './pages/consultation/ConsultationRoom';
import ChatRoom from './pages/consultation/ChatRoom';
import PrescriptionForm from './pages/prescription/PrescriptionForm';
import PatientPrescriptionView from './pages/patient/prescription/PatientPrescriptionView';
import FoodLinks from './pages/admin/content/FoodLinks';
import ConnectivityManager from './components/ConnectivityManager';
import RuleAcceptanceModal from './components/RuleAcceptanceModal';
import AdminRules from './pages/admin/rules/AdminRules';
import SupportTickets from './pages/admin/support/SupportTickets';
import PatientSupport from './pages/patient/support/Support';
import BlueTickManagement from './pages/admin/BlueTickManagement';
import GetVerified from './pages/doctor/verification/GetVerified';
// New imports
import FeatureControls from './pages/admin/FeatureControls';
import ConsultationSettings from './pages/admin/ConsultationSettings';
import AllUsersManagement from './pages/admin/users/UserManagement';
import ReelsPage from './pages/ReelsPage';
import ReelsProfilePage from './pages/ReelsProfilePage';
import ReelsAdmin from './pages/admin/reels/ReelsAdmin';
import ReelSubscriptionSettings from './pages/admin/reels/ReelSubscriptionSettings';
import ReelSubscriptionPage from './pages/ReelSubscription';
import AppointmentAnalytics from './pages/admin/AppointmentAnalytics';
import PartnerManagement from './pages/admin/PartnerManagement';
import ComingSoonCard from './components/ComingSoonCard';

import { notificationAPI, authAPI } from './utils/api';
import { initPushNotifications, addPushListeners } from './utils/pushNotifications';
import BackButtonHandler from './components/BackButtonHandler';
import IncomingCallOverlay from './components/IncomingCallOverlay';
import PermissionGate from './components/PermissionGate';
import { initStoreKit } from './utils/storeKit';
import { callKitService } from './services/CallKitService';

const GoogleOAuthHandler = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const stateParam = params.get('state');

    if (!code) return;

    let role = 'patient';
    try {
      if (stateParam) {
        const state = JSON.parse(stateParam);
        role = state.role || 'patient';
      }
    } catch (e) {}

    const processCode = async () => {
      try {
        const res = await authAPI.googleExchange({
          code,
          redirect_uri: window.location.origin,
          role,
        });
        if (res.data?.success && res.data?.data) {
          const { token, user } = res.data.data;
          localStorage.setItem('token', token);
          if (user) {
            localStorage.setItem('user', JSON.stringify(user));
            localStorage.setItem('userRole', user.role || '');
          }
          window.history.replaceState({}, document.title, window.location.pathname);
          if (user.role === 'patient') navigate('/patient/home', { replace: true });
          else navigate(`/${user.role}/dashboard`, { replace: true });
        } else {
          window.history.replaceState({}, document.title, window.location.pathname);
          navigate('/role-selection', { replace: true });
        }
      } catch (error) {
        console.error('Google OAuth callback error:', error);
        window.history.replaceState({}, document.title, window.location.pathname);
        navigate('/role-selection', { replace: true });
      }
    };

    processCode();
  }, [navigate]);

  return null;
};

const NotificationActionHandler = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const checkPendingNotif = () => {
      const consultationId = localStorage.getItem('pending_notif_consultation');
      const token = localStorage.getItem('token');
      if (consultationId && token) {
        console.log('🚀 Redirecting to pending consultation:', consultationId);
        localStorage.removeItem('pending_notif_consultation');
        navigate(`/consultation/${consultationId}`);
      }
    };

    checkPendingNotif();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkPendingNotif();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [navigate]);
  return null;
};

function App() {
  useEffect(() => {
    console.log('🚀 App Mounting...');

    // Sentry initialization safely
    try {
      Sentry.init({ dsn: process.env.REACT_APP_SENTRY_DSN || 'production-mode' });
    } catch (e) {
      console.warn('Sentry init failed', e);
    }

    // Initialize Native Plugins
    const timer = setTimeout(async () => {
      try {
        console.log('📲 Initializing Native Plugins...');

        await initPushNotifications();
        addPushListeners();
        initStoreKit();
        callKitService.initialize();
      } catch (err) {
        console.error('Plugin initialization error:', err);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <Router>
      <NotificationActionHandler />
      <GoogleOAuthHandler />
      <ErrorBoundary>
        <AuthProvider>
          <SocketProvider>
            <CallProvider>
              <NotificationProvider>
                <div className="min-h-screen bg-dark text-light">
                  <ConnectivityManager>
                    <RuleAcceptanceModal />
                    <BackButtonHandler />
                    <PermissionGate>
                      <IncomingCallOverlay />
                      <Routes>
                        <Route path="/" element={<SplashScreen />} />
                        <Route path="/onboarding" element={<Onboarding />} />
                        <Route path="/role-selection" element={<RoleSelection />} />
                        <Route path="/verify/prescription/:prescriptionId" element={<VerifyPrescription />} />
                        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                        <Route path="/privacy" element={<PrivacyPolicy />} />
                        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                        <Route path="/terms" element={<TermsAndConditions />} />
                        <Route path="/about-us" element={<AboutUs />} />
                        <Route path="/support" element={<SupportPage />} />

                        <Route path="/auth/patient/login" element={<PatientLogin />} />
                        <Route path="/auth/doctor/login" element={<DoctorLogin />} />
                        <Route path="/auth/retailer/login" element={<RetailerLogin />} />
                        <Route path="/auth/clinic/login" element={<ClinicLogin />} />
                        <Route path="/auth/hospital/login" element={<HospitalLogin />} />

                        <Route path="/auth/patient/register" element={<PatientRegister />} />
                        <Route path="/auth/doctor/register" element={<DoctorRegister />} />
                        <Route path="/auth/retailer/register" element={<RetailerRegister />} />
                        <Route path="/auth/clinic/register" element={<ClinicRegister />} />
                        <Route path="/auth/hospital/register" element={<HospitalRegister />} />

                        <Route path="/admin/login" element={<AdminLogin />} />
                        <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
                        <Route path="/auth/admin/login" element={<Navigate to="/admin/login" replace />} />

                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />

                        <Route path="/subscription" element={<ProtectedRoute><Subscription /></ProtectedRoute>} />
                        <Route path="/onboarding/profile" element={<ProtectedRoute><ProfileImageOnboarding /></ProtectedRoute>} />
                        <Route path="/onboarding/documents" element={<ProtectedRoute><DocumentUpload /></ProtectedRoute>} />
                        <Route path="/onboarding/pending" element={<ProtectedRoute><ApprovalWaiting /></ProtectedRoute>} />
                        <Route path="/consultation/:consultationId" element={<ProtectedRoute><ConsultationRoom /></ProtectedRoute>} />
                        <Route path="/prescription/create/:consultationId" element={<ProtectedRoute><PrescriptionForm /></ProtectedRoute>} />
                        <Route path="/patient/prescription/:consultationId" element={<ProtectedRoute><PatientPrescriptionView /></ProtectedRoute>} />
                        <Route path="/chat/:consultationId" element={<ProtectedRoute><ChatRoom /></ProtectedRoute>} />
                        <Route path="/reels" element={<ProtectedRoute><ReelsPage /></ProtectedRoute>} />
                        <Route path="/reels/profile" element={<ProtectedRoute><ReelsProfilePage /></ProtectedRoute>} />
                        <Route path="/reels/subscription" element={<ProtectedRoute><ReelSubscriptionPage /></ProtectedRoute>} />
                        <Route path="/ambulance" element={<ProtectedRoute><div className="p-6 max-w-md mx-auto pt-8"><ComingSoonCard title="Ambulance Service" icon="🚑" description="Emergency ambulance service is on its way. We're working to bring you reliable and fast ambulance booking." /></div></ProtectedRoute>} />
                        <Route path="/food-service" element={<ProtectedRoute><div className="p-6 max-w-md mx-auto pt-8"><ComingSoonCard title="Food Service" icon="🍽️" description="Restaurant food delivery and meal ordering will be available soon. Stay tuned for updates." /></div></ProtectedRoute>} />
                        <Route path="/doctor/get-verified" element={<ProtectedRoute allowedRoles={['doctor', 'clinic_doctor', 'hospital_doctor']}><GetVerified /></ProtectedRoute>} />

                        <Route path="/admin/*" element={
                          <ProtectedRoute allowedRoles={['admin']}>
                            <Routes>
                              <Route path="dashboard" element={<AdminDashboard />} />
                              <Route path="users/all" element={<AllUsers />} />
                              <Route path="users/pending" element={<PendingUsers />} />
                              <Route path="content/sliders" element={<Sliders />} />
                              <Route path="content/yoga" element={<YogaVideos />} />
                              <Route path="content/podcasts" element={<Podcasts />} />
                              <Route path="content/food-links" element={<FoodLinks />} />
                              <Route path="subscriptions/plans" element={<SubscriptionPlans />} />
                              <Route path="rules" element={<AdminRules />} />
                              <Route path="support" element={<SupportTickets />} />
                              <Route path="settings" element={<AdminSettings />} />
                              <Route path="feature-controls" element={<FeatureControls />} />
                              <Route path="consultation-settings" element={<ConsultationSettings />} />
                              <Route path="users/manage" element={<AllUsersManagement />} />
                              <Route path="users/manage/:userId" element={<AllUsersManagement />} />
                              <Route path="reels" element={<ReelsAdmin />} />
                              <Route path="reel-subscription-settings" element={<ReelSubscriptionSettings />} />
                              <Route path="appointments/analytics" element={<AppointmentAnalytics />} />
                              <Route path="partners" element={<PartnerManagement />} />
                              <Route path="withdrawals" element={<Withdrawals />} />
                              <Route path="blue-tick" element={<BlueTickManagement />} />
                              <Route path="*" element={<Navigate to="dashboard" replace />} />
                            </Routes>
                          </ProtectedRoute>
                        } />

                        <Route path="/*" element={<ProtectedRoute><MobileLayout /></ProtectedRoute>} />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </PermissionGate>
                  </ConnectivityManager>
                </div>
              </NotificationProvider>
            </CallProvider>
          </SocketProvider>
        </AuthProvider>
      </ErrorBoundary>
    </Router>
  );
}

export default App;
